import React, { Fragment } from "react";
import { highlightHexForColorId } from "../lib/verseHighlightsApi";

/**
 * Split verse body into plain / highlighted spans from DB rows (offsets in body string).
 * Overlapping ranges: boundaries are merged; each minimal segment uses the first matching highlight by sort order.
 *
 * @param {string} body
 * @param {{ id: string, start_offset: number, end_offset: number, color: string }[]} highlights
 */
export function renderBodyWithPartialHighlights(body, highlights) {
  if (!body) return null;
  if (!highlights?.length) {
    return <span className="whitespace-pre-wrap">{body}</span>;
  }

  const n = body.length;
  const valid = highlights.filter(
    (h) =>
      h.start_offset < h.end_offset &&
      h.start_offset >= 0 &&
      h.end_offset <= n
  );
  if (!valid.length) {
    return <span className="whitespace-pre-wrap">{body}</span>;
  }

  const sorted = [...valid].sort(
    (a, b) => a.start_offset - b.start_offset || a.end_offset - b.end_offset
  );

  const breaks = new Set([0, n]);
  sorted.forEach((h) => {
    breaks.add(h.start_offset);
    breaks.add(h.end_offset);
  });
  const pts = [...breaks].sort((a, b) => a - b);

  const parts = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const slice = body.slice(a, b);
    if (!slice) continue;

    const hl = sorted.find((h) => h.start_offset <= a && h.end_offset >= b);
    const key = `${a}-${b}-${hl?.id ?? "plain"}`;

    if (hl) {
      parts.push(
        <mark
          key={key}
          className="rounded-sm px-0.5 [box-decoration-break:clone]"
          style={{
            backgroundColor: highlightHexForColorId(hl.color),
            color: "inherit",
          }}
          data-highlight-id={hl.id}
        >
          {slice}
        </mark>
      );
    } else {
      parts.push(
        <span key={key} className="whitespace-pre-wrap">
          {slice}
        </span>
      );
    }
  }

  return <Fragment>{parts}</Fragment>;
}
